// Catalog.ID Client — consents the ceremony console prepares itself
// ============================================================================
// docs/cid-governance.md M6, D6, D20. Nobody hands an organization a binding
// consent to sign: the owner names the operator code and the console builds
// the consent from what the node knows — the organization, its current
// `main_signing_key`, and the next sequence of the code's operator record
// (`GET /api/federation/operators/{code}`); the owner never types a sequence.
// The migration consents are prepared the same way. What is prepared is then
// shown through the same renderer and ledger checks as a delivered document.
//
// Each builder runs the shared library's payload builder, which reads what it
// builds back strictly, so a consent `cl-root` would refuse as malformed is
// refused here instead of being signed.

import {
    HYBRID_PROFILE,
    FederationFileError,
    accountMigrationConsentPayload,
    bindingConsentPayload,
    bulkMigrationConsentPayload,
    formatTimestamp,
    isOperatorCode,
    type AccountMigrationConsent,
    type BindingConsent,
    type BulkMigrationConsent,
} from '@catalog-federation';
import type { OperatorRecord } from './ledger-context';

export type Prepared<T> = { ok: true; consent: T } | { ok: false; message: string };

/** An operator code as a person typed it: surrounding spaces and case do not matter. */
export function normalizeOperatorCode(typed: string): string {
    return typed.trim().toLowerCase();
}

/** Why `code` is not an operator code, or null when it is. */
export function invalidCode(code: string): string | null {
    return isOperatorCode(code)
        ? null
        : `"${code}" is not an operator code: two to four of a-z and 0-9.`;
}

function built<T>(consent: T, check: (c: T) => unknown): Prepared<T> {
    try {
        check(consent);
        return { ok: true, consent };
    } catch (e) {
        if (e instanceof FederationFileError) return { ok: false, message: `This consent cannot be signed: ${e.message}` };
        throw e;
    }
}

/**
 * The organization's consent to be bound to `operatorId` at the sequence after
 * its operator record's, effective now. `record` is what the node answered
 * for the code: null (404) means the code is not reserved.
 */
export function prepareBindingConsent(input: {
    operatorId: string;
    orgCid: string;
    orgPublicKey: Uint8Array | null;
    record: OperatorRecord | null;
    now: Date;
}): Prepared<BindingConsent> {
    const operatorId = normalizeOperatorCode(input.operatorId);
    const bad = invalidCode(operatorId);
    if (bad) return { ok: false, message: bad };
    if (input.orgPublicKey === null) {
        return { ok: false, message: `This node publishes no signing key for ${input.orgCid}; there is nothing to bind.` };
    }
    if (input.record === null) {
        return {
            ok: false,
            message: `Operator ${operatorId} is not reserved in this node's ledger, so it cannot be bound. The `
                + 'Developer reserves a code first, when the operator agreement is signed; check the code, or wait '
                + 'until the node has read the reservation.',
        };
    }
    if (input.record.status === 'retired') {
        return {
            ok: false,
            message: `Operator ${operatorId} is retired: its record is closed for good and it is never bound again.`,
        };
    }
    return built<BindingConsent>({
        operatorId,
        sequence: input.record.sequence + 1,
        orgCid: input.orgCid,
        orgPublicKey: input.orgPublicKey,
        orgSigningProfile: HYBRID_PROFILE,
        effectiveFrom: formatTimestamp(input.now),
    }, bindingConsentPayload);
}

/**
 * An account's consent to move its origin from this node's code to
 * `toOperatorId` (D20). `fromOperatorId` is this node's own code — a session
 * exists only at the account's origin (D18).
 */
export function prepareAccountMigrationConsent(input: {
    accountCid: string;
    fromOperatorId: string | null;
    toOperatorId: string;
    sequence: number;
    accountPublicKey: Uint8Array | null;
}): Prepared<AccountMigrationConsent> {
    if (input.fromOperatorId === null) {
        return { ok: false, message: 'This node did not say which operator code it serves; the move cannot be prepared.' };
    }
    const to = normalizeOperatorCode(input.toOperatorId);
    const bad = invalidCode(to);
    if (bad) return { ok: false, message: bad };
    if (input.accountPublicKey === null) {
        return { ok: false, message: `This node publishes no signing key for ${input.accountCid}.` };
    }
    if (!Number.isSafeInteger(input.sequence) || input.sequence < 1) {
        return { ok: false, message: 'The sequence is a whole number from 1: 1 for an account\'s first move.' };
    }
    return built<AccountMigrationConsent>({
        accountCid: input.accountCid,
        sequence: input.sequence,
        fromOperatorId: input.fromOperatorId,
        toOperatorId: to,
        accountPublicKey: input.accountPublicKey,
        accountSigningProfile: HYBRID_PROFILE,
    }, accountMigrationConsentPayload);
}

/**
 * The consent of the organization bound to `fromOperatorId` to move every
 * account of that code to `toOperatorId` (D20). The code must be one of the
 * organization's bound codes (`operatorCodes`).
 */
export function prepareBulkMigrationConsent(input: {
    fromOperatorId: string;
    toOperatorId: string;
    sequence: number;
    orgCid: string;
    orgPublicKey: Uint8Array | null;
    boundCodes: readonly string[];
}): Prepared<BulkMigrationConsent> {
    const from = normalizeOperatorCode(input.fromOperatorId);
    const to = normalizeOperatorCode(input.toOperatorId);
    const bad = invalidCode(from) ?? invalidCode(to);
    if (bad) return { ok: false, message: bad };
    if (!input.boundCodes.includes(from)) {
        return {
            ok: false,
            message: `Operator ${from} is not bound to ${input.orgCid}: only the organization bound to a code consents `
                + 'to moving its accounts.',
        };
    }
    if (input.orgPublicKey === null) {
        return { ok: false, message: `This node publishes no signing key for ${input.orgCid}.` };
    }
    if (!Number.isSafeInteger(input.sequence) || input.sequence < 1) {
        return { ok: false, message: 'The sequence is a whole number from 1: 1 for a code\'s first bulk move.' };
    }
    return built<BulkMigrationConsent>({
        fromOperatorId: from,
        toOperatorId: to,
        sequence: input.sequence,
        orgCid: input.orgCid,
        orgPublicKey: input.orgPublicKey,
        orgSigningProfile: HYBRID_PROFILE,
    }, bulkMigrationConsentPayload);
}
